/**
 * AdminExperienceCurationPage — /app/admin/experiences
 * Studio Split: desktop two-column, mobile select dropdown.
 */
import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { X, Plus, GripVertical, Sparkles, Search, ExternalLink, MoreHorizontal, Pencil, Trash2, Check, List } from 'lucide-react';
import { features } from '@/config/features';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCoverImageUrl } from '@/hooks/useMediaAssets';
import { cn } from '@/lib/utils';
import { TagSuggestionsSection } from '@/components/admin/TagSuggestionsSection';
import { MatchRulesEditor } from '@/components/admin/MatchRulesEditor';

import { PageHeader } from '@/components/admin/PageHeader';
import { StudioSurface } from '@/components/admin/StudioSurface';
import { EmptyBlock } from '@/components/admin/EmptyBlock';
import { StudioNavList, type StudioNavItem } from '@/components/admin/StudioNavList';
import { AdminCreateSheet } from '@/components/admin/AdminCreateSheet';
import { useAdminFabEffect } from '@/hooks/useAdminFabEffect';

interface Experience {
  slug: string;
  title: string;
  intro: string;
  status: string;
  order_index: number;
}

interface LinkedPlace {
  place_id: string;
  experience_slug: string;
  order_index: number;
  note: string | null;
  place_name?: string;
  place_slug?: string;
  place_municipality?: string | null;
  place_landskap?: string | null;
}

interface SearchPlace {
  id: string;
  slug: string;
  name: string;
  municipality: string | null;
  landskap: string | null;
}

const MAX_DISPLAYED = 10;
const BROWSE_PAGE_SIZE = 20;

export default function AdminExperienceCurationPage() {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const queryClient = useQueryClient();

  // Register global FAB for mobile create — Admin Studio v1
  useAdminFabEffect({
    id: 'admin-experiences-create',
    ariaLabel: 'Skapa upplevelse',
    onClick: () => setCreateSheetOpen(true),
    visibility: 'mobile',
  });

  const { data: experiences = [], isLoading: loadingExperiences } = useQuery({
    queryKey: ['admin-experiences'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('experiences')
        .select('slug, title, intro, status, order_index')
        .order('order_index', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Experience[];
    },
  });

  // Fetch place counts per experience_slug in one query
  const { data: placeCounts = {} } = useQuery({
    queryKey: ['admin-experience-place-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('place_experiences')
        .select('experience_slug');
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data ?? []).forEach((row: any) => {
        counts[row.experience_slug] = (counts[row.experience_slug] || 0) + 1;
      });
      return counts;
    },
  });

  const navItems: StudioNavItem[] = useMemo(
    () =>
      experiences.map((exp) => ({
        id: exp.slug,
        title: exp.title,
        subtitle: `/${exp.slug}`,
        badges: [
          { label: `${placeCounts[exp.slug] ?? 0} platser`, variant: 'outline' as const },
          exp.status === 'public'
            ? { label: 'Publicerad', variant: 'default' as const }
            : { label: 'Utkast', variant: 'secondary' as const },
        ],
      })),
    [experiences, placeCounts],
  );

  const reorderMut = useMutation({
    mutationFn: async (orderedSlugs: string[]) => {
      await Promise.all(
        orderedSlugs.map((slug, i) =>
          supabase
            .from('experiences')
            .update({ order_index: (i + 1) * 10 })
            .eq('slug', slug)
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
      toast.success('Ordning sparad');
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
      toast.error('Kunde inte spara ordning');
    },
  });

  const handleReorderExperiences = useCallback(
    (reorderedItems: StudioNavItem[]) => {
      // Optimistic: update query cache
      const slugOrder = reorderedItems.map((i) => i.id);
      const reordered = slugOrder
        .map((slug) => experiences.find((e) => e.slug === slug))
        .filter(Boolean) as Experience[];
      queryClient.setQueryData(['admin-experiences'], reordered);
      reorderMut.mutate(slugOrder);
    },
    [experiences, queryClient, reorderMut],
  );

  const hasExperiences = !loadingExperiences && experiences.length > 0;
  const noExperiences = !loadingExperiences && experiences.length === 0;

  return (
    <div>
      <PageHeader
        title="Upplevelser – Kuraterade platser"
        description="Koppla platser till upplevelsesidor (sektionen &quot;Utvalda platser&quot;)"
        actions={
          hasExperiences ? (
            <Button size="sm" className="hidden md:inline-flex" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Skapa upplevelse
            </Button>
          ) : undefined
        }
      />

      <div className="px-4 lg:px-8 pb-8">
        {noExperiences ? (
          <StudioSurface>
            <EmptyBlock
              title="Inga upplevelser ännu"
              description="Skapa en upplevelse för att kunna koppla utvalda platser."
              action={
                <Button onClick={() => setCreateOpen(true)}>
                  Skapa upplevelse
                </Button>
              }
            />
          </StudioSurface>
        ) : (
          <>
            {/* Mobile: Select dropdown */}
            <div className="lg:hidden mb-4">
              <Select
                value={selectedSlug ?? ''}
                onValueChange={(v) => setSelectedSlug(v || null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Välj upplevelse…" />
                </SelectTrigger>
                <SelectContent>
                  {experiences.map((exp) => (
                    <SelectItem key={exp.slug} value={exp.slug}>
                      {exp.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Desktop: Studio Split */}
            <div className="hidden lg:grid grid-cols-[280px_1fr] gap-8 items-start">
              {/* Left: nav list */}
              <StudioSurface className="sticky top-4">
                <h2 className="text-xs font-semibold uppercase text-muted-foreground tracking-wide mb-3">
                  Upplevelser
                </h2>
                <StudioNavList
                  items={navItems}
                  selectedId={selectedSlug ?? undefined}
                  onSelect={setSelectedSlug}
                  onReorder={handleReorderExperiences}
                />
              </StudioSurface>

              {/* Right: curation surface */}
              <StudioSurface>
                {selectedSlug ? (
                  <ExperienceLinks
                    slug={selectedSlug}
                    queryClient={queryClient}
                    experiences={experiences}
                    onDeleted={() => setSelectedSlug(null)}
                  />
                ) : (
                  <EmptyBlock title="Välj en upplevelse" description="Klicka på en upplevelse i listan till vänster." />
                )}
              </StudioSurface>
            </div>

            {/* Mobile: curation surface */}
            <div className="lg:hidden">
              <StudioSurface>
                {selectedSlug ? (
                  <ExperienceLinks
                    slug={selectedSlug}
                    queryClient={queryClient}
                    experiences={experiences}
                    onDeleted={() => setSelectedSlug(null)}
                  />
                ) : (
                  <EmptyBlock title="Välj en upplevelse" description="Använd dropdown ovan." />
                )}
              </StudioSurface>
            </div>
          </>
        )}
      </div>

      <CreateExperienceDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(slug) => {
          setSelectedSlug(slug);
          queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
          queryClient.invalidateQueries({ queryKey: ['admin-experience-place-counts'] });
        }}
      />

      {/* Mobile create sheet (triggered by global FAB) */}
      <CreateExperienceSheet
        open={createSheetOpen}
        onOpenChange={setCreateSheetOpen}
        onCreated={(slug) => {
          setSelectedSlug(slug);
          queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
          queryClient.invalidateQueries({ queryKey: ['admin-experience-place-counts'] });
        }}
      />
    </div>
  );
}

/* ── Create experience dialog ── */

function CreateExperienceDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (slug: string) => void;
}) {
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [status, setStatus] = useState('public');

  const slugValid = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);

  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('experiences')
        .insert({ slug, title, intro, status });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Upplevelse skapad');
      onCreated(slug);
      onOpenChange(false);
      setSlug('');
      setTitle('');
      setIntro('');
      setStatus('public');
    },
    onError: (e: any) => toast.error(e.message ?? 'Kunde inte skapa upplevelse'),
  });

  const canSubmit = slug.length > 0 && slugValid && title.trim().length > 0 && intro.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Skapa upplevelse</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="exp-slug">Slug</Label>
            <Input
              id="exp-slug"
              placeholder="t.ex. vara-tillsammans"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
            />
            {slug.length > 0 && !slugValid && (
              <p className="text-xs text-destructive">Bara gemener, siffror och bindestreck.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="exp-title">Titel</Label>
            <Input
              id="exp-title"
              placeholder="Vara tillsammans"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="exp-intro">Ingress</Label>
            <Textarea
              id="exp-intro"
              placeholder="Kort beskrivning av upplevelsen…"
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Publicerad</SelectItem>
                <SelectItem value="draft">Utkast</SelectItem>
                <SelectItem value="archived">Arkiverad</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!canSubmit || createMutation.isPending}
          >
            {createMutation.isPending ? 'Sparar…' : 'Skapa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Create experience sheet (mobile) ── */

function CreateExperienceSheet({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (slug: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [status, setStatus] = useState('public');

  const slug = title
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const slugValid = slug.length > 0 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);

  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('experiences')
        .insert({ slug, title, intro, status });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Upplevelse skapad');
      onCreated(slug);
      onOpenChange(false);
      setTitle('');
      setIntro('');
      setStatus('public');
    },
    onError: (e: any) => toast.error(e.message ?? 'Kunde inte skapa upplevelse'),
  });

  const canSubmit = slugValid && title.trim().length > 0 && intro.trim().length > 0;

  return (
    <AdminCreateSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Skapa upplevelse"
      footerActions={
        <>
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button
            className="flex-1"
            onClick={() => createMutation.mutate()}
            disabled={!canSubmit || createMutation.isPending}
          >
            {createMutation.isPending ? 'Sparar…' : 'Skapa'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="sheet-title">Titel</Label>
          <Input
            id="sheet-title"
            placeholder="Vara tillsammans"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ fontSize: 16 }}
          />
          {title.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Slug: <code className="bg-muted px-1 py-0.5 rounded">{slug || '–'}</code>
              {!slugValid && title.length > 0 && (
                <span className="text-destructive ml-1">Ogiltig slug</span>
              )}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sheet-intro">Ingress</Label>
          <Textarea
            id="sheet-intro"
            placeholder="Kort beskrivning av upplevelsen…"
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            rows={3}
            style={{ fontSize: 16 }}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger style={{ fontSize: 16 }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Publicerad</SelectItem>
              <SelectItem value="draft">Utkast</SelectItem>
              <SelectItem value="archived">Arkiverad</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </AdminCreateSheet>
  );
}

/* ── Edit experience dialog ── */

function EditExperienceDialog({
  open,
  onOpenChange,
  experience,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  experience: Experience;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(experience.title);
  const [intro, setIntro] = useState(experience.intro);
  const [status, setStatus] = useState(experience.status);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('experiences')
        .update({ title, intro, status })
        .eq('slug', experience.slug);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Upplevelse uppdaterad');
      onSaved();
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e.message ?? 'Kunde inte spara'),
  });

  const canSubmit = title.trim().length > 0 && intro.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Redigera upplevelse</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Slug</Label>
            <Input value={experience.slug} disabled className="bg-muted" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-title">Titel</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-intro">Ingress</Label>
            <Textarea
              id="edit-intro"
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Publicerad</SelectItem>
                <SelectItem value="draft">Utkast</SelectItem>
                <SelectItem value="archived">Arkiverad</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Avbryt
          </Button>
          <Button
            onClick={() => updateMutation.mutate()}
            disabled={!canSubmit || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Sparar…' : 'Spara'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Delete experience confirmation ── */

function DeleteExperienceDialog({
  open,
  onOpenChange,
  experience,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  experience: Experience;
  onDeleted: () => void;
}) {
  const [confirmText, setConfirmText] = useState('');
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      // Double-check no linked places
      const { count, error: countError } = await supabase
        .from('place_experiences')
        .select('place_id', { count: 'exact', head: true })
        .eq('experience_slug', experience.slug);
      if (countError) throw countError;
      if ((count ?? 0) > 0) throw new Error('Upplevelsen har fortfarande kopplade platser. Ta bort dem först.');

      const { error } = await supabase
        .from('experiences')
        .delete()
        .eq('slug', experience.slug);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Upplevelse borttagen');
      queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
      queryClient.invalidateQueries({ queryKey: ['admin-experience-place-counts'] });
      onDeleted();
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e.message ?? 'Kunde inte ta bort'),
  });

  const confirmed = confirmText === experience.slug;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Ta bort upplevelse</AlertDialogTitle>
          <AlertDialogDescription>
            Detta tar bort upplevelsen <strong>"{experience.title}"</strong> permanent.
            Skriv <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">{experience.slug}</code> nedan för att bekräfta.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input
          placeholder={experience.slug}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="mt-2"
        />
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setConfirmText('')}>Avbryt</AlertDialogCancel>
          <AlertDialogAction
            disabled={!confirmed || deleteMutation.isPending}
            onClick={(e) => {
              e.preventDefault();
              deleteMutation.mutate();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {deleteMutation.isPending ? 'Tar bort…' : 'Ta bort'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ── Experience links with drag & drop ── */

function ExperienceLinks({
  slug,
  queryClient,
  experiences,
  onDeleted,
}: {
  slug: string;
  queryClient: any;
  experiences: Experience[];
  onDeleted: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [addTab, setAddTab] = useState<'search' | 'browse'>('search');
  const [browseLimit, setBrowseLimit] = useState(BROWSE_PAGE_SIZE);
  const [browseFilter, setBrowseFilter] = useState<'public' | 'all'>('public');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const currentExperience = experiences.find((e) => e.slug === slug);

  // Fetch match_rules for this experience
  const { data: experienceMatchRules } = useQuery({
    queryKey: ['experience-match-rules', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('experiences')
        .select('match_rules')
        .eq('slug', slug)
        .single();
      if (error) throw error;
      return data?.match_rules as Record<string, unknown> | null;
    },
  });
  const { data: allLinked = [], isLoading } = useQuery({
    queryKey: ['admin-place-experiences', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('place_experiences')
        .select('place_id, experience_slug, order_index, note')
        .eq('experience_slug', slug)
        .order('order_index');
      if (error) throw error;

      const ids = (data ?? []).map((d: any) => d.place_id);
      if (ids.length === 0) return [];

      const { data: places } = await supabase
        .from('places')
        .select('id, slug, name, municipality, landskap')
        .in('id', ids);

      const placeMap = new Map((places ?? []).map((p: any) => [p.id, p]));
      return (data ?? []).map((d: any) => ({
        ...d,
        place_name: placeMap.get(d.place_id)?.name ?? '(okänd)',
        place_slug: placeMap.get(d.place_id)?.slug ?? '',
        place_municipality: placeMap.get(d.place_id)?.municipality ?? null,
        place_landskap: placeMap.get(d.place_id)?.landskap ?? null,
      })) as LinkedPlace[];
    },
  });

  // Display max 10
  const linked = allLinked.slice(0, MAX_DISPLAYED);
  const totalCount = allLinked.length;

  const { data: searchResults = [] } = useQuery({
    queryKey: ['admin-place-search', searchQuery],
    queryFn: async () => {
      if (searchQuery.length < 2) return [];
      const { data, error } = await supabase
        .from('places')
        .select('id, slug, name, municipality, landskap')
        .ilike('name', `%${searchQuery}%`)
        .limit(10);
      if (error) throw error;
      return (data ?? []) as SearchPlace[];
    },
    enabled: searchQuery.length >= 2,
  });

  // Browse all places (paginated)
  const { data: browseData, isLoading: browseLoading } = useQuery({
    queryKey: ['admin-place-browse', browseFilter, browseLimit],
    queryFn: async () => {
      let q = supabase
        .from('places')
        .select('id, slug, name, municipality, landskap', { count: 'exact' })
        .order('updated_at', { ascending: false })
        .limit(browseLimit);
      if (browseFilter === 'public') {
        q = q.eq('status', 'public');
      }
      const { data, error, count } = await q;
      if (error) throw error;
      return { places: (data ?? []) as SearchPlace[], total: count ?? 0 };
    },
    enabled: addTab === 'browse',
    placeholderData: (prev) => prev,
  });

  const addMutation = useMutation({
    mutationFn: async (placeId: string) => {
      const maxOrder = allLinked.length > 0 ? Math.max(...allLinked.map((l) => l.order_index)) : 0;
      const { error } = await supabase
        .from('place_experiences')
        .insert({ place_id: placeId, experience_slug: slug, order_index: maxOrder + 10 });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-place-experiences', slug] });
      queryClient.invalidateQueries({ queryKey: ['admin-experience-place-counts'] });
      setSearchQuery('');
      toast.success('Plats tillagd');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const removeMutation = useMutation({
    mutationFn: async (placeId: string) => {
      const { error } = await supabase
        .from('place_experiences')
        .delete()
        .eq('place_id', placeId)
        .eq('experience_slug', slug);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-place-experiences', slug] });
      queryClient.invalidateQueries({ queryKey: ['admin-experience-place-counts'] });
      toast.success('Plats borttagen');
    },
    onError: (e: any) => toast.error(e.message),
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: { place_id: string; order_index: number }[]) => {
      const updates = items.map((item) =>
        supabase
          .from('place_experiences')
          .update({ order_index: item.order_index })
          .eq('place_id', item.place_id)
          .eq('experience_slug', slug)
      );
      const results = await Promise.all(updates);
      const firstError = results.find((r) => r.error);
      if (firstError?.error) throw firstError.error;
    },
    onError: () => {
      toast.error('Kunde inte spara ordning');
      queryClient.invalidateQueries({ queryKey: ['admin-place-experiences', slug] });
    },
    onSuccess: () => {
      toast.success('Ordning sparad');
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = linked.findIndex((l) => l.place_id === active.id);
      const newIndex = linked.findIndex((l) => l.place_id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(linked, oldIndex, newIndex);

      queryClient.setQueryData(
        ['admin-place-experiences', slug],
        reordered.map((item, i) => ({ ...item, order_index: (i + 1) * 10 }))
      );

      reorderMutation.mutate(
        reordered.map((item, i) => ({ place_id: item.place_id, order_index: (i + 1) * 10 }))
      );
    },
    [linked, slug, queryClient, reorderMutation],
  );

  const linkedIds = new Set(allLinked.map((l) => l.place_id));
  const isDndDisabled = reorderMutation.isPending;

  const handleDeleteClick = async () => {
    // Check linked places count before showing delete dialog
    const count = allLinked.length;
    if (count > 0) {
      toast.error(`Upplevelsen har ${count} kopplade platser. Ta bort dem först.`);
      return;
    }
    setDeleteOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">
          Utvalda platser
          {totalCount > 0 && (
            <span className="text-muted-foreground font-normal ml-1.5">({totalCount})</span>
          )}
        </h2>
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" asChild>
            <a href={`/upplevelse/${slug}`} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Förhandsvisa
            </a>
          </Button>
          {currentExperience && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setEditOpen(true)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Redigera upplevelse
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDeleteClick}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Ta bort upplevelse
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {reorderMutation.isPending && (
        <p className="text-xs text-muted-foreground">Sparar ordning…</p>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : linked.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground text-sm">
            Inga platser kopplade ännu. Sök och lägg till en plats nedan.
          </p>
        </div>
      ) : (
        <>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={linked.map((l) => l.place_id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {linked.map((item) => (
                  <SortablePlaceRow
                    key={item.place_id}
                    item={item}
                    onRemove={() => removeMutation.mutate(item.place_id)}
                    disabled={isDndDisabled}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
          {totalCount > MAX_DISPLAYED && (
            <p className="text-xs text-muted-foreground">
              Visar {MAX_DISPLAYED} av {totalCount}
            </p>
          )}
        </>
      )}

      {/* Search / Browse tabs */}
      <div>
        <h3 className="text-sm font-medium mb-2">Lägg till plats</h3>
        {/* Segmented toggle */}
        <div className="flex gap-1 mb-3 bg-muted rounded-lg p-0.5 w-fit">
          <button
            onClick={() => setAddTab('search')}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5',
              addTab === 'search'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <Search className="h-3.5 w-3.5" />
            Sök
          </button>
          <button
            onClick={() => { setAddTab('browse'); setBrowseLimit(BROWSE_PAGE_SIZE); }}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5',
              addTab === 'browse'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <List className="h-3.5 w-3.5" />
            Alla platser
          </button>
        </div>

        {addTab === 'search' && (
          <>
            <div className="relative max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Sök platser…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 space-y-1 max-w-sm">
                {searchResults.map((place) => (
                  <BrowsePlaceRow
                    key={place.id}
                    place={place}
                    isLinked={linkedIds.has(place.id)}
                    onAdd={() => addMutation.mutate(place.id)}
                    adding={addMutation.isPending}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {addTab === 'browse' && (
          <div className="space-y-3">
            {/* Filter chips */}
            <div className="flex gap-1.5">
              <Badge
                variant={browseFilter === 'public' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => { setBrowseFilter('public'); setBrowseLimit(BROWSE_PAGE_SIZE); }}
              >
                Publicerade
              </Badge>
              <Badge
                variant={browseFilter === 'all' ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => { setBrowseFilter('all'); setBrowseLimit(BROWSE_PAGE_SIZE); }}
              >
                Alla
              </Badge>
            </div>

            {browseLoading && !browseData ? (
              <div className="space-y-1.5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  {(browseData?.places ?? []).map((place) => (
                    <BrowsePlaceRow
                      key={place.id}
                      place={place}
                      isLinked={linkedIds.has(place.id)}
                      onAdd={() => addMutation.mutate(place.id)}
                      adding={addMutation.isPending}
                    />
                  ))}
                </div>
                {browseData && browseData.total > browseLimit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBrowseLimit((l) => l + BROWSE_PAGE_SIZE)}
                    className="w-full"
                  >
                    Visa fler ({browseData.total - browseLimit} kvar)
                  </Button>
                )}
                {browseData && (
                  <p className="text-xs text-muted-foreground">
                    Visar {Math.min(browseLimit, browseData.total)} av {browseData.total} platser
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Match rules editor (B-modellen) */}
      {features.showTagSuggestions && (
        <MatchRulesEditor
          entityId={slug}
          matchRules={experienceMatchRules}
        />
      )}

      {/* Tag-based suggestions (B-modellen) */}
      {features.showTagSuggestions && (
        <TagSuggestionsSection
          entitySlug={slug}
          linkedPlaceIds={linkedIds}
          onAdd={(placeId) => addMutation.mutate(placeId)}
          addPending={addMutation.isPending}
        />
      )}

      {/* Edit dialog */}
      {currentExperience && editOpen && (
        <EditExperienceDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          experience={currentExperience}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-experiences'] });
          }}
        />
      )}

      {/* Delete dialog */}
      {currentExperience && deleteOpen && (
        <DeleteExperienceDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          experience={currentExperience}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}

/* ── Sortable place row ── */

function SortablePlaceRow({
  item,
  onRemove,
  disabled,
}: {
  item: LinkedPlace;
  onRemove: () => void;
  disabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.place_id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  const coverUrl = useCoverImageUrl('place', item.place_id, 320);
  const locationText = [item.place_municipality, item.place_landskap].filter(Boolean).join(', ');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 p-2.5 rounded-lg border bg-card transition-shadow',
        isDragging && 'shadow-lg opacity-90'
      )}
    >
      <button
        {...attributes}
        {...listeners}
        className="shrink-0 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted text-muted-foreground/40 hover:text-muted-foreground transition-colors"
        aria-label="Dra för att ändra ordning"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Thumbnail */}
      <div className="shrink-0 w-11 h-11 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
        {coverUrl ? (
          <img src={coverUrl} alt={item.place_name ?? ''} className="w-full h-full object-cover" />
        ) : (
          <Sparkles className="h-4 w-4 text-muted-foreground/30" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">{item.place_name}</p>
        <p className="text-xs text-muted-foreground truncate">
          {locationText && <span>{locationText} · </span>}
          /{item.place_slug}
        </p>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        disabled={disabled}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

/* ── Browse / Search result row (shared) ── */

function BrowsePlaceRow({
  place,
  isLinked,
  onAdd,
  adding,
}: {
  place: SearchPlace;
  isLinked: boolean;
  onAdd: () => void;
  adding: boolean;
}) {
  const coverUrl = useCoverImageUrl('place', place.id, 320);
  const locationText = [place.municipality, place.landskap].filter(Boolean).join(', ');

  return (
    <div className="flex items-center gap-2.5 p-2 rounded-lg border text-sm">
      <div className="shrink-0 w-8 h-8 rounded-md overflow-hidden bg-muted flex items-center justify-center">
        {coverUrl ? (
          <img src={coverUrl} alt={place.name} className="w-full h-full object-cover" />
        ) : (
          <Sparkles className="h-3 w-3 text-muted-foreground/30" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span className="truncate block">{place.name}</span>
        <span className="text-xs text-muted-foreground block truncate">
          {locationText && <>{locationText} · </>}
          /{place.slug}
        </span>
      </div>
      {isLinked ? (
        <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0 pr-1">
          <Check className="h-3.5 w-3.5" />
          Tillagd
        </span>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={onAdd}
          disabled={adding}
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
